// Lays out a filled report worksheet (exceljs) as an HTML table model, so the
// report shown in the app is the same workbook that downloads: same merged
// cells, column widths, fonts, fills and borders as the lab's template.
//
// Only what the Performance Test Report template uses is handled: plain
// values (it has no formulas), fixed-decimal number formats, solid fills,
// and thin/hair/medium borders.

// Excel sizes columns in characters of the default font and rows in points
const columnPx = (width) => Math.round((width || 8.43) * 7 + 5)
const pointsToPx = (points) => Math.round(points * 4 / 3)

// Legacy palette indexes the template uses; 64 is "automatic" (black)
const INDEXED_COLORS = { 8: '#000000', 9: '#ffffff', 10: '#ff0000', 12: '#0000ff', 62: '#333399', 64: '#000000' }
const THEME_TEXT_COLORS = { 0: '#ffffff', 1: '#000000' }

const BORDER_STYLES = {
  hair: '1px dotted',
  dotted: '1px dotted',
  thin: '1px solid',
  dashed: '1px dashed',
  medium: '2px solid',
  mediumDashed: '2px dashed',
  thick: '3px solid',
  double: '3px double'
}

const toColor = (color) => {
  if (!color) return null
  if (color.argb) return `#${color.argb.slice(-6)}`
  if (color.indexed !== undefined) return INDEXED_COLORS[color.indexed] || null
  if (color.theme !== undefined) return THEME_TEXT_COLORS[color.theme] || null
  return null
}

const toBorder = (edge) => {
  if (!edge || !BORDER_STYLES[edge.style]) return undefined
  return `${BORDER_STYLES[edge.style]} ${toColor(edge.color) || '#000000'}`
}

const columnNumber = (letters) => [...letters].reduce((n, ch) => n * 26 + ch.charCodeAt(0) - 64, 0)

// "B43:G43" -> { top: 43, left: 2, bottom: 43, right: 7 }
const parseRange = (range) => {
  const [a, b = a] = range.split(':')
  const [, c1, r1] = a.match(/^([A-Z]+)(\d+)$/)
  const [, c2, r2] = b.match(/^([A-Z]+)(\d+)$/)
  return { top: Number(r1), left: columnNumber(c1), bottom: Number(r2), right: columnNumber(c2) }
}

// "0.0" -> 1 decimal; anything else is shown as stored
const formatValue = (value, numFmt) => {
  if (value === null || value === undefined) return ''
  if (typeof value === 'number') {
    const fixed = /^0(?:\.(0+))?$/.exec(numFmt || '')
    return fixed ? value.toFixed(fixed[1] ? fixed[1].length : 0) : String(value)
  }
  if (value instanceof Date) return value.toISOString().slice(0, 19).replace('T', ' ')
  if (typeof value === 'object') {
    if (Array.isArray(value.richText)) return value.richText.map(part => part.text).join('')
    if ('result' in value) return formatValue(value.result, numFmt)
    if (value.text !== undefined) return String(value.text)
  }
  return String(value)
}

const cellStyle = (sheet, cell, span) => {
  const font = cell.font || {}
  const alignment = cell.alignment || {}
  const fill = cell.fill || {}
  // A merged block's outer edges are drawn by the cells along them
  const edge = (row, col) => sheet.getCell(row, col).border || {}
  const style = {
    borderTop: toBorder(edge(span.top, span.left).top),
    borderLeft: toBorder(edge(span.top, span.left).left),
    borderRight: toBorder(edge(span.top, span.right).right),
    borderBottom: toBorder(edge(span.bottom, span.left).bottom),
    fontSize: font.size ? `${pointsToPx(font.size)}px` : undefined,
    fontWeight: font.bold ? 700 : undefined,
    fontStyle: font.italic ? 'italic' : undefined,
    textDecoration: font.underline ? 'underline' : undefined,
    color: toColor(font.color) || undefined,
    background: fill.type === 'pattern' && fill.pattern === 'solid' ? toColor(fill.fgColor) || undefined : undefined,
    verticalAlign: alignment.vertical === 'middle' ? 'middle' : alignment.vertical === 'top' ? 'top' : 'bottom',
    whiteSpace: alignment.wrapText ? 'pre-wrap' : 'pre'
  }
  const horizontal = alignment.horizontal
  if (horizontal === 'center' || horizontal === 'centerContinuous') style.textAlign = 'center'
  else if (horizontal === 'right') style.textAlign = 'right'
  else if (horizontal === 'left') style.textAlign = 'left'
  else style.textAlign = typeof cell.value === 'number' ? 'right' : 'left'
  Object.keys(style).forEach(key => style[key] === undefined && delete style[key])
  return style
}

export const sheetToTable = (sheet) => {
  const used = sheet.dimensions
  const top = used.top
  const left = used.left
  const bottom = used.bottom
  const right = used.right

  const spans = {}
  const covered = new Set()
  ;(sheet.model.merges || []).forEach(range => {
    const span = parseRange(range)
    spans[`${span.top}:${span.left}`] = span
    for (let r = span.top; r <= span.bottom; r++) {
      for (let c = span.left; c <= span.right; c++) {
        if (r !== span.top || c !== span.left) covered.add(`${r}:${c}`)
      }
    }
  })

  const columns = []
  for (let c = left; c <= right; c++) columns.push(columnPx(sheet.getColumn(c).width))
  const defaultHeight = sheet.properties.defaultRowHeight || 15

  const rows = []
  for (let r = top; r <= bottom; r++) {
    const cells = []
    for (let c = left; c <= right; c++) {
      if (covered.has(`${r}:${c}`)) continue
      const span = spans[`${r}:${c}`] || { top: r, left: c, bottom: r, right: c }
      // A block that runs past the used area is clipped to it
      const spanBottom = Math.min(span.bottom, bottom)
      const spanRight = Math.min(span.right, right)
      const cell = sheet.getCell(r, c)
      cells.push({
        key: c,
        rowspan: spanBottom - r + 1,
        colspan: spanRight - c + 1,
        // Single-line text the panel shrinks to fit, as Excel does for the
        // template's shrink-to-fit labels
        fit: !cell.alignment?.wrapText,
        text: formatValue(cell.value, cell.numFmt),
        style: cellStyle(sheet, cell, { top: r, left: c, bottom: spanBottom, right: spanRight })
      })
    }
    rows.push({ key: r, height: pointsToPx(sheet.getRow(r).height || defaultHeight), cells })
  }

  return {
    columns,
    width: columns.reduce((sum, w) => sum + w, 0),
    rows
  }
}
